<template>
  <div class="flex items-center p-0.5 rounded-md bg-neutral-25 flex-nowrap cursor-default">
    <BaseText type="body" size="sm" class="text-text-normal px-2 py-0.5">
      Filters
    </BaseText>
    <div class="h-3 w-0 mx-0.5 border-l border-border-hover" />
    <!-- Filters Count / Show Filters Button -->
    <div class="relative">
      <button class="px-2 py-0.5 rounded transition-colors duration-100"
      :class="showFilters ? 'bg-neutral-50' : 'hover:bg-neutral-50'"
      @click="showFilters = !showFilters">
        <BaseText type="body" size="sm" class="text-text-muted">
          {{ numberOfFilters }}
        </BaseText>
      </button>
      <!-- Filter List Dropdown -->
      <transition>
        <div v-if="showFilters" class="preset-filters-list flex flex-col gap-1.5 w-max"
        v-on-clickaway="() => { showFilters = false }">
          <div v-if="includedItems.length && excludedItems.length" 
          class="w-full flex items-center justify-center col-span-3 p-1 rounded-md bg-secondary-green-10">
            <IncludedIcon style="color: #184E44;" />
          </div>
          <template v-for="item in includedItems">
            <BaseText type="body" size="sm" class="text-text-normal flex-shrink-0">
              {{ item.name }}
            </BaseText>
            <BaseText type="label" size="sm" class="text-text-muted px-1.5">
              {{ item.label }}
            </BaseText>
            <button class="p-0.5 rounded transition-colors duration-100 hover:bg-neutral-50 ml-auto"
            @click="$emit('removeFilter', { key: item.key, value: item.value, isComponent: item.isComponent })">
              <TagRemoveIcon stroke="#808899" />
            </button>
          </template>
          <div v-if="excludedItems.length" 
          class="w-full flex items-center justify-center col-span-3 p-1 rounded-md bg-secondary-red-10">
            <ExcludedIcon style="color: #710E21;" />
          </div>
          <!-- Yeah I copy pasted this bc I'm too lazy to make it a component -->
          <template v-for="item in excludedItems">
            <BaseText type="body" size="sm" class="text-text-normal flex-shrink-0">
              {{ item.name }}
            </BaseText>
            <BaseText type="label" size="sm" class="text-text-muted px-1.5">
              {{ item.label }}
            </BaseText>
            <button class="p-0.5 rounded transition-colors duration-100 hover:bg-neutral-50 ml-auto"
            @click="$emit('removeFilter', { key: item.key, value: item.value, isComponent: item.isComponent })">
              <TagRemoveIcon stroke="#808899" />
            </button>
          </template>
        </div>
      </transition>
    </div>
    <div class="h-3 w-0 mx-0.5 border-l border-border-hover" />
    <BaseText type="label" size="sm" class="text-text-muted pl-2 py-0.5 pr-1">
      {{ presetInfo.name }}
    </BaseText>
    <!-- Remove Preset Button -->
    <button class="p-0.5 rounded transition-colors duration-100 hover:bg-neutral-50"
    @click="$emit('removePreset')">
      <TagRemoveIcon stroke="#808899" />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'

import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import IncludedIcon from '../../globals/Icons/FilterIcons/IncludedIcon.vue'
import ExcludedIcon from '../../globals/Icons/FilterIcons/ExcludedIcon.vue'

export default {
  name: 'PresetFiltersItem',
  mixins: [clickaway],
  components: {
    TagRemoveIcon,
    IncludedIcon,
    ExcludedIcon
  },
  props: {
    presetInfo: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      showFilters: false
    }
  },
  computed: {
    ...mapGetters('SearchModule', ['getFilterCategories']),
    numberOfFilters () {
      let count = 0
      const { componentFilters, ...basicFilters } = this.filters
      count += Object.entries(basicFilters).reduce((acc, [_, valueArray]) => acc + valueArray.length, 0)
      if (componentFilters) {
        count += Object.entries(componentFilters).reduce((acc, [_, value]) => acc + Array.isArray(value) ? value.length : 1, 0)
      }
      return count
    },
    filterListItems () {
      const { componentFilters, ...basicFilters } = this.filters
      const basicListItems = Object.entries(basicFilters).flatMap(([key, valueArray]) => {
        const [logicalOperator, categoryId] = key.split('.')
        const category = this.getFilterCategories.find(category => category.id === categoryId)
        return valueArray.map(value => {
          const optionName = category.options.find(option => category.valueTransform(option[category.optionValueKey]) === value)?.name ?? value
          return {
            key,
            name: category.name,
            value,
            label: optionName,
            isExclude: logicalOperator === 'exclude',
            isComponent: false
          }
        })
      })
      const componentListItems = componentFilters ? Object.entries(componentFilters).map(([key, value]) => {
        const category = this.getFilterCategories.find(category => category.id === key)
        return {
          key,
          name: category.name,
          value,
          label: category.getValueText(value),
          isExclude: false,
          isComponent: true
        }
      }) : []
      return [...basicListItems, ...componentListItems]
    },
    includedItems () {
      return this.filterListItems.filter(item => !item.isExclude)
    },
    excludedItems () {
      return this.filterListItems.filter(item => item.isExclude)
    }
  }
}
</script>

<style scoped>
.preset-filters-list {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  
  min-width: 212px;
  max-width: 312px;
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 6px;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0 12px 16px -8px rgba(24, 35, 68, 0.04), 0 2px 4px -1.5px rgba(24, 35, 68, 0.04), 0 1px 2px -0.75px rgba(24, 35, 68, 0.04), 0 0 0 1px rgba(0, 40, 136, 0.08);
  z-index: 1000;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>